import { useState, useMemo } from 'react';
import { cars } from '../../mocks/cars';
import { boats } from '../../mocks/boats';
import { campers } from '../../mocks/campers';
import { motorcycles } from '../../mocks/motorcycles';

interface Vehicle {
  id: string;
  name: string;
  model: string;
  category: string;
  image: string;
  pricePerDay: number;
}

const allVehicles: Vehicle[] = [
  ...boats.map(b => ({
    id: `boat-${b.id}`,
    name: b.name,
    model: b.model,
    category: 'Bateau',
    image: b.image,
    pricePerDay: b.pricePerDay,
  })),
  ...cars.map(c => ({
    id: `car-${c.id}`,
    name: c.name,
    model: c.model,
    category: 'Voiture',
    image: c.image,
    pricePerDay: c.pricePerDay,
  })),
  ...campers.map(cm => ({
    id: `camper-${cm.id}`,
    name: cm.name,
    model: cm.model,
    category: 'Camping-Car',
    image: cm.image,
    pricePerDay: cm.pricePerDay,
  })),
  ...motorcycles.map(m => ({
    id: `moto-${m.id}`,
    name: m.name,
    model: m.model,
    category: 'Moto',
    image: m.image,
    pricePerDay: m.pricePerDay,
  })),
];

const timeOptions = [
  '07:00',
  '07:30',
  '08:00',
  '08:30',
  '09:00',
  '09:30',
  '10:00',
  '10:30',
  '11:00',
  '11:30',
  '12:00',
  '12:30',
  '13:00',
  '13:30',
  '14:00',
  '14:30',
  '15:00',
  '15:30',
  '16:00',
  '16:30',
  '17:00',
  '17:30',
  '18:00',
  '18:30',
  '19:00',
  '19:30',
  '20:00',
  '20:30',
  '21:00',
];

export default function BookingForm() {
  const [currentStep, setCurrentStep] = useState(0);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [selectingEnd, setSelectingEnd] = useState(false);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('18:00');
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth());
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const [selectedVehicles, setSelectedVehicles] = useState<string[]>([]);
  const [showAddSecond, setShowAddSecond] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [numberOfGuests, setNumberOfGuests] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [filterCategory, setFilterCategory] = useState<string>('all');

  const monthNames = [
    'Janvier',
    'Février',
    'Mars',
    'Avril',
    'Mai',
    'Juin',
    'Juillet',
    'Août',
    'Septembre',
    'Octobre',
    'Novembre',
    'Décembre',
  ];
  const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

  const today = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(
      2,
      '0'
    )}`;
  }, []);

  const getDaysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year: number, month: number) => new Date(year, month, 1).getDay();

  const handlePrevMonth = () => {
    if (calendarMonth === 0) {
      setCalendarMonth(11);
      setCalendarYear(calendarYear - 1);
    } else setCalendarMonth(calendarMonth - 1);
  };

  const handleNextMonth = () => {
    if (calendarMonth === 11) {
      setCalendarMonth(0);
      setCalendarYear(calendarYear + 1);
    } else setCalendarMonth(calendarMonth + 1);
  };

  const handleDateClick = (day: number) => {
    const dateStr = `${calendarYear}-${String(calendarMonth + 1).padStart(2, '0')}-${String(day).padStart(
      2,
      '0'
    )}`;
    if (dateStr < today) return;

    if (!selectingEnd || !startDate) {
      setStartDate(dateStr);
      setEndDate(null);
      setSelectingEnd(true);
    } else {
      if (dateStr < startDate) {
        setStartDate(dateStr);
        setEndDate(null);
      } else {
        setEndDate(dateStr);
        setSelectingEnd(false);
      }
    }
  };

  const isInRange = (dateStr: string) => {
    if (!startDate || !endDate) return false;
    return dateStr >= startDate && dateStr <= endDate;
  };

  const formatDateFr = (dateStr: string) => {
    const [y, m, d] = dateStr.split('-');
    return `${d}/${m}/${y}`;
  };

  const getDayCount = () => {
    if (!startDate || !endDate) return 0;
    const s = new Date(startDate);
    const e = new Date(endDate);
    return Math.ceil((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  };

  const filteredVehicles = filterCategory === 'all' ? allVehicles : allVehicles.filter(v => v.category === filterCategory);

  const toggleVehicle = (vehicleId: string) => {
    if (selectedVehicles.includes(vehicleId)) {
      const newSelection = selectedVehicles.filter(v => v !== vehicleId);
      setSelectedVehicles(newSelection);
      if (newSelection.length <= 2) setShowAddSecond(false);
    } else {
      if (!showAddSecond && selectedVehicles.length === 1) {
        setShowAddSecond(true);
      } else if (selectedVehicles.length < 2 || showAddSecond) {
        setSelectedVehicles([...selectedVehicles, vehicleId]);
      }
    }
  };

  const getSelectedVehicleDetails = () => {
    return selectedVehicles
      .map(id => allVehicles.find(v => v.id === id))
      .filter((v): v is Vehicle => Boolean(v));
  };

  const getTotalEstimate = () => {
    const days = getDayCount();
    const vehicles = getSelectedVehicleDetails();
    return vehicles.reduce((sum, v) => sum + v.pricePerDay * days, 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!startDate || !endDate || selectedVehicles.length === 0 || !customerName || !customerPhone) return;

    setSubmitting(true);
    try {
      const vehicles = getSelectedVehicleDetails();
      const vehicleNames = vehicles.map(v => `${v.name} - ${v.model} (${v.category})`).join(' | ');

      const formBody = new URLSearchParams();
      formBody.append('name', customerName);
      formBody.append('phone', customerPhone);
      formBody.append('email', customerEmail);
      formBody.append('date_debut', `${formatDateFr(startDate)} à ${startTime}`);
      formBody.append('date_fin', `${formatDateFr(endDate)} à ${endTime}`);
      formBody.append('duree', `${getDayCount()} jour(s)`);
      formBody.append('vehicules', vehicleNames);
      formBody.append('nombre_personnes', numberOfGuests);
      formBody.append('demandes_speciales', notes);
      formBody.append('estimation_prix', `${getTotalEstimate()}€`);

      const response = await fetch('https://readdy.ai/api/form/d6et7geqd308q1a0ar70', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setCurrentStep(0);
    setStartDate(null);
    setEndDate(null);
    setSelectingEnd(false);
    setStartTime('09:00');
    setEndTime('18:00');
    setSelectedVehicles([]);
    setShowAddSecond(false);
    setCustomerName('');
    setCustomerPhone('');
    setCustomerEmail('');
    setNotes('');
    setNumberOfGuests('');
    setSubmitStatus('idle');
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(calendarYear, calendarMonth);
    const firstDay = getFirstDayOfMonth(calendarYear, calendarMonth);
    const days: React.ReactNode[] = [];

    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-9" />);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${calendarYear}-${String(calendarMonth + 1).padStart(2, '0')}-${String(day).padStart(
        2,
        '0'
      )}`;
      const isPast = dateStr < today;
      const isStart = startDate === dateStr;
      const isEnd = endDate === dateStr;
      const inRange = isInRange(dateStr);

      let classes = 'h-9 w-9 rounded-lg text-sm font-medium transition-all ';
      if (isPast) {
        classes += 'text-gray-300 cursor-not-allowed';
      } else if (isStart || isEnd) {
        classes += 'bg-gradient-to-br from-navy-800 to-ocean-500 text-white shadow-md cursor-pointer';
      } else if (inRange) {
        classes += 'bg-ocean-100 text-ocean-700 cursor-pointer hover:bg-ocean-200';
      } else {
        classes += 'text-navy-700 cursor-pointer hover:bg-sand-100';
      }

      days.push(
        <button key={day} type="button" onClick={() => handleDateClick(day)} disabled={isPast} className={classes}>
          {day}
        </button>
      );
    }
    return days;
  };

  const steps = ['Dates', 'Véhicule', 'Infos'];

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-6">
      {steps.map((step, index) => (
        <div key={index} className="flex items-center">
          <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium transition-all ${
            currentStep > index
              ? 'bg-gradient-to-br from-ocean-400 to-ocean-600 text-white shadow-sm'
              : currentStep === index
              ? 'bg-gradient-to-br from-navy-800 to-navy-700 text-white shadow-md ring-2 ring-ocean-300 ring-offset-1'
              : 'bg-sand-200 text-navy-400'
          }`}>
            {currentStep > index ? <i className="ri-check-line text-sm"></i> : index + 1}
          </div>
          <span className={`ml-2 text-sm font-medium hidden sm:inline ${
            currentStep >= index ? 'text-navy-700' : 'text-navy-300'
          }`}>
            {step}
          </span>
          {index < 2 && (
            <div className={`w-8 sm:w-12 h-0.5 mx-2 sm:mx-3 transition-all ${
              currentStep > index ? 'bg-gradient-to-r from-ocean-400 to-ocean-500' : 'bg-sand-200'
            }`} />
          )}
        </div>
      ))}
    </div>
  );

  if (submitStatus === 'success') {
    return (
      <div className="text-center py-10">
        <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-ocean-400 to-ocean-600 rounded-full mx-auto mb-4 shadow-lg">
          <i className="ri-check-line text-3xl text-white"></i>
        </div>
        <h3 className="text-2xl font-bold text-navy-800 mb-2">Réservation Envoyée !</h3>
        <p className="text-navy-600 mb-6">Nous vous contacterons sous 24h pour confirmer les détails.</p>
        <button
          onClick={resetForm}
          className="px-6 py-3 bg-gradient-to-r from-navy-800 to-navy-700 hover:from-navy-900 hover:to-navy-800 text-white font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap shadow-md hover:shadow-lg"
        >
          Nouvelle Réservation
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl">
      {renderStepIndicator()}

      {/* STEP 0: Dates */}
      {currentStep === 0 && (
        <div>
          <h3 className="text-lg font-semibold text-navy-800 mb-2 text-center">Choisissez vos dates de location</h3>
          <p className="text-sm text-navy-500 mb-4 text-center">
            {!startDate ? 'Sélectionnez la date de début' : !endDate ? 'Sélectionnez la date de fin' : `${getDayCount()} jour(s) sélectionné(s)`}
          </p>

          <div className="bg-gradient-to-br from-sand-50 to-ocean-50 rounded-xl p-3 mb-4 border border-sand-200">
            <div className="flex items-center justify-between mb-3">
              <button type="button" onClick={handlePrevMonth} className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-sand-200 transition-colors cursor-pointer">
                <i className="ri-arrow-left-s-line text-lg text-navy-600"></i>
              </button>
              <h4 className="text-base font-semibold text-navy-800">{monthNames[calendarMonth]} {calendarYear}</h4>
              <button type="button" onClick={handleNextMonth} className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-sand-200 transition-colors cursor-pointer">
                <i className="ri-arrow-right-s-line text-lg text-navy-600"></i>
              </button>
            </div>
            <div className="grid grid-cols-7 gap-0.5 mb-1">
              {dayNames.map(d => (
                <div key={d} className="h-9 flex items-center justify-center text-xs font-medium text-ocean-500">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-0.5">{renderCalendar()}</div>
          </div>

          {startDate && (
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="p-3 bg-gradient-to-br from-ocean-50 to-ocean-100 rounded-xl border border-ocean-200">
                <p className="text-xs text-ocean-600 font-medium mb-1 flex items-center"><i className="ri-calendar-line mr-1"></i>Début</p>
                <p className="text-sm font-bold text-navy-800">{formatDateFr(startDate)}</p>
              </div>
              <div className={`p-3 rounded-xl ${endDate ? 'bg-gradient-to-br from-ocean-50 to-ocean-100 border border-ocean-200' : 'bg-sand-100 border-2 border-dashed border-sand-300'}`}>
                <p className="text-xs text-ocean-600 font-medium mb-1 flex items-center"><i className="ri-calendar-check-line mr-1"></i>Fin</p>
                <p className="text-sm font-bold text-navy-800">{endDate ? formatDateFr(endDate) : '...'}</p>
              </div>
            </div>
          )}

          {startDate && endDate && (
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-xs font-medium text-navy-700 mb-1"><i className="ri-time-line mr-1 text-ocean-500"></i>Heure de début</label>
                <select value={startTime} onChange={e => setStartTime(e.target.value)} className="w-full px-3 py-2.5 border border-sand-300 rounded-lg text-sm cursor-pointer focus:ring-2 focus:ring-ocean-400 focus:border-transparent bg-white">
                  {timeOptions.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-navy-700 mb-1"><i className="ri-time-line mr-1 text-ocean-500"></i>Heure de fin</label>
                <select value={endTime} onChange={e => setEndTime(e.target.value)} className="w-full px-3 py-2.5 border border-sand-300 rounded-lg text-sm cursor-pointer focus:ring-2 focus:ring-ocean-400 focus:border-transparent bg-white">
                  {timeOptions.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={() => setCurrentStep(1)}
            disabled={!startDate || !endDate}
            className="w-full px-6 py-3 bg-gradient-to-r from-navy-800 to-ocean-500 hover:from-navy-900 hover:to-ocean-600 disabled:from-sand-200 disabled:to-sand-200 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap shadow-md hover:shadow-lg"
          >
            Suivant <i className="ri-arrow-right-line ml-2"></i>
          </button>
        </div>
      )}

      {/* STEP 1: Vehicle */}
      {currentStep === 1 && (
        <div>
          <h3 className="text-lg font-semibold text-navy-800 mb-1 text-center">Choisissez votre véhicule</h3>
          <p className="text-sm text-navy-500 mb-4 text-center">
            {selectedVehicles.length === 0 ? 'Sélectionnez au moins un véhicule' : `${selectedVehicles.length} véhicule(s) sélectionné(s)`}
          </p>

          <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
            {[
              { key: 'all', label: 'Tous' },
              { key: 'Bateau', label: 'Bateaux' },
              { key: 'Voiture', label: 'Voitures' },
              { key: 'Camping-Car', label: 'Camping-Cars' },
              { key: 'Moto', label: 'Motos' },
            ].map(cat => (
              <button
                key={cat.key}
                type="button"
                onClick={() => setFilterCategory(cat.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-all ${
                  filterCategory === cat.key
                    ? 'bg-gradient-to-r from-navy-800 to-ocean-500 text-white shadow-sm'
                    : 'bg-sand-100 text-navy-600 hover:bg-sand-200'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          <div className="space-y-2 mb-4 max-h-72 overflow-y-auto pr-1">
            {filteredVehicles.map(vehicle => {
              const isSelected = selectedVehicles.includes(vehicle.id);
              const isDisabled = !isSelected && selectedVehicles.length >= 2;
              return (
                <button
                  key={vehicle.id}
                  type="button"
                  onClick={() => !isDisabled && toggleVehicle(vehicle.id)}
                  disabled={isDisabled}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left ${
                    isSelected
                      ? 'border-ocean-400 bg-gradient-to-r from-ocean-50 to-navy-50 shadow-sm'
                      : isDisabled
                      ? 'border-sand-100 bg-sand-50 opacity-50 cursor-not-allowed'
                      : 'border-sand-200 hover:border-ocean-300 bg-white cursor-pointer'
                  }`}
                >
                  <div className="w-16 h-12 rounded-lg overflow-hidden flex-shrink-0">
                    <img src={vehicle.image} alt={vehicle.name} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-navy-800 truncate">{vehicle.name}</p>
                    <p className="text-xs text-navy-500 truncate">{vehicle.model}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs px-1.5 py-0.5 bg-sand-100 rounded text-navy-600">{vehicle.category}</span>
                      <span className="text-xs font-semibold text-ocean-500">{vehicle.pricePerDay}€/jour</span>
                    </div>
                  </div>
                  {isSelected && (
                    <div className="w-6 h-6 flex items-center justify-center bg-gradient-to-br from-navy-800 to-ocean-500 rounded-full flex-shrink-0 shadow-sm">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {selectedVehicles.length === 1 && !showAddSecond && (
            <div className="p-3 bg-sand-50 border border-sand-200 rounded-xl mb-4">
              <div className="flex items-center gap-2">
                <i className="ri-add-circle-line text-coral-500 text-lg"></i>
                <div className="flex-1">
                  <p className="text-sm font-medium text-navy-800">Envie de louer un 2ème véhicule ?</p>
                  <p className="text-xs text-navy-500">Sélectionnez un autre véhicule dans la liste ci-dessus</p>
                </div>
              </div>
            </div>
          )}

          {selectedVehicles.length > 0 && (
            <div className="p-3 bg-gradient-to-br from-ocean-50 to-navy-50 rounded-xl mb-4 border border-ocean-200">
              <p className="text-xs font-semibold text-ocean-700 mb-2 flex items-center"><i className="ri-check-double-line mr-1"></i>Véhicule(s) sélectionné(s) :</p>
              {getSelectedVehicleDetails().map(v => (
                <div key={v.id} className="flex items-center justify-between text-sm mb-1">
                  <span className="text-navy-700">{v.name} - {v.model}</span>
                  <span className="font-semibold text-ocean-500">{v.pricePerDay}€/j</span>
                </div>
              ))}
              <div className="border-t border-ocean-200 mt-2 pt-2 flex justify-between">
                <span className="text-sm font-medium text-navy-700">Estimation ({getDayCount()} jours)</span>
                <span className="text-sm font-bold text-navy-800">{getTotalEstimate()}€</span>
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <button type="button" onClick={() => setCurrentStep(0)} className="flex-1 px-4 py-3 border border-sand-300 text-navy-700 font-medium rounded-lg hover:bg-sand-50 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-arrow-left-line mr-1"></i> Retour
            </button>
            <button
              type="button"
              onClick={() => setCurrentStep(2)}
              disabled={selectedVehicles.length === 0}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-navy-800 to-ocean-500 hover:from-navy-900 hover:to-ocean-600 disabled:from-sand-200 disabled:to-sand-200 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap shadow-md hover:shadow-lg"
            >
              Suivant <i className="ri-arrow-right-line ml-1"></i>
            </button>
          </div>
        </div>
      )}

      {/* STEP 2: Confirm */}
      {currentStep === 2 && (
        <form id="booking-form" data-readdy-form onSubmit={handleSubmit}>
          <h3 className="text-lg font-semibold text-navy-800 mb-4 text-center">Confirmez votre réservation</h3>

          <div className="bg-gradient-to-br from-ocean-50 to-navy-50 rounded-xl p-3 mb-4 border border-ocean-200">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-navy-800 flex items-center"><i className="ri-file-list-3-line mr-1.5 text-ocean-500"></i>Récapitulatif</h4>
              <button type="button" onClick={() => setCurrentStep(0)} className="text-ocean-500 hover:text-ocean-600 text-xs font-medium cursor-pointer">Modifier</button>
            </div>
            <div className="space-y-1.5 text-sm">
              <div className="flex items-center text-navy-700">
                <i className="ri-calendar-line text-ocean-500 mr-2 text-base"></i>
                <span>Du <strong>{startDate && formatDateFr(startDate)}</strong> à <strong>{startTime}</strong></span>
              </div>
              <div className="flex items-center text-navy-700">
                <i className="ri-calendar-check-line text-ocean-500 mr-2 text-base"></i>
                <span>Au <strong>{endDate && formatDateFr(endDate)}</strong> à <strong>{endTime}</strong></span>
              </div>
              <div className="flex items-center text-navy-700">
                <i className="ri-time-line text-ocean-500 mr-2 text-base"></i>
                <span>Durée : <strong>{getDayCount()} jour(s)</strong></span>
              </div>
              {getSelectedVehicleDetails().map(v => (
                <div key={v.id} className="flex items-center text-navy-700">
                  <i className="ri-roadster-line text-ocean-500 mr-2 text-base"></i>
                  <span>{v.name} - {v.model} <span className="text-ocean-500 font-medium">({v.pricePerDay}€/j)</span></span>
                </div>
              ))}
              <div className="border-t border-ocean-200 pt-1.5 mt-1.5 flex justify-between font-semibold">
                <span className="text-navy-800">Estimation totale</span>
                <span className="text-navy-800">{getTotalEstimate()}€</span>
              </div>
            </div>
          </div>

          <div className="space-y-3 mb-4">
            <div>
              <label className="block text-xs font-medium text-navy-700 mb-1">Nom complet *</label>
              <input type="text" name="name" value={customerName} onChange={e => setCustomerName(e.target.value)} required className="w-full px-3 py-2.5 border border-sand-300 rounded-lg focus:ring-2 focus:ring-ocean-400 focus:border-transparent text-sm bg-white" placeholder="Jean Dupont" />
            </div>
            <div>
              <label className="block text-xs font-medium text-navy-700 mb-1">Téléphone *</label>
              <input type="tel" name="phone" value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} required className="w-full px-3 py-2.5 border border-sand-300 rounded-lg focus:ring-2 focus:ring-ocean-400 focus:border-transparent text-sm bg-white" placeholder="+33 6 XX XX XX XX" />
            </div>
            <div>
              <label className="block text-xs font-medium text-navy-700 mb-1">Email</label>
              <input type="email" name="email" value={customerEmail} onChange={e => setCustomerEmail(e.target.value)} className="w-full px-3 py-2.5 border border-sand-300 rounded-lg focus:ring-2 focus:ring-ocean-400 focus:border-transparent text-sm bg-white" placeholder="jean@exemple.com" />
            </div>
            <div>
              <label className="block text-xs font-medium text-navy-700 mb-1">Nombre de personnes</label>
              <select name="guests" value={numberOfGuests} onChange={e => setNumberOfGuests(e.target.value)} className="w-full px-3 py-2.5 border border-sand-300 rounded-lg focus:ring-2 focus:ring-ocean-400 focus:border-transparent text-sm cursor-pointer bg-white">
                <option value="">Sélectionnez</option>
                <option value="1-2">1-2 personnes</option>
                <option value="3-4">3-4 personnes</option>
                <option value="5-6">5-6 personnes</option>
                <option value="7-8">7-8 personnes</option>
                <option value="9-10">9-10 personnes</option>
                <option value="11-12">11-12 personnes</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-navy-700 mb-1">Demandes spéciales</label>
              <textarea name="notes" value={notes} onChange={e => setNotes(e.target.value)} rows={3} maxLength={500} className="w-full px-3 py-2.5 border border-sand-300 rounded-lg focus:ring-2 focus:ring-ocean-400 focus:border-transparent text-sm resize-none bg-white" placeholder="Questions, demandes particulières..." />
            </div>
          </div>

          {submitStatus === 'error' && (
            <div className="p-3 bg-coral-50 border border-coral-200 rounded-lg mb-3">
              <p className="text-coral-600 text-sm flex items-center"><i className="ri-error-warning-line mr-1.5"></i>Une erreur s&apos;est produite. Veuillez réessayer.</p>
            </div>
          )}

          <div className="flex gap-3">
            <button type="button" onClick={() => setCurrentStep(1)} className="flex-1 px-4 py-3 border border-sand-300 text-navy-700 font-medium rounded-lg hover:bg-sand-50 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-arrow-left-line mr-1"></i> Retour
            </button>
            <button
              type="submit"
              disabled={submitting || !customerName || !customerPhone}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-coral-500 to-coral-600 hover:from-coral-600 hover:to-coral-700 disabled:from-sand-200 disabled:to-sand-200 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap shadow-md hover:shadow-lg"
            >
              {submitting ? <span className="flex items-center justify-center"><i className="ri-loader-4-line animate-spin mr-2"></i>Envoi...</span> : <span><i className="ri-check-line mr-1"></i>Confirmer la Réservation</span>}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
